// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the three modes. A mode must change how the game PLAYS, not just a number, so none of
// them touches the board size or the piece count (skyhold already spent a mode on "bigger board,
// more pieces", and it is the laziest axis there is). The axes here are the DECK, the OPENING
// GEOMETRY, and — for Arrears — the EXCHANGE RULE itself, which is the game's signature verb.
//
// The HOST's pick travels frozen inside rematch.ts's round start; everything else is DERIVED from
// mode + seed on every peer, so a mode can never desync. modeOf() guards an id off the wire with
// Object.hasOwn (a bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined fields).

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Which half of the deck this mode draws from — see src/tolls.ts. */
  pool: 'steady' | 'wild';
  /** How many tolls are dealt at the start. Two pairs plus the table. */
  deal: number;
  /** How many tolls sit on the table between the two hands. */
  table: number;
  /**
   * Arrears only. When the table holds two tolls, the one you just paid lands on the table and you
   * may take EITHER of the two that were already there — but never the one you just paid. Without
   * that clause you could pay a toll and instantly reclaim it, hoarding one weapon forever and
   * deleting the whole point of the game.
   */
  neverTakeBack: boolean;
  /** Opening geometry: 'flat' = all five on the home row, 'forward' = two bearers pushed up. */
  setup: 'flat' | 'forward';
  /**
   * THE LEVY. Turns without a piece being taken before the tide collects: whoever has more pieces
   * standing forfeits their rearmost Bearer (both sides if level).
   *
   * This is the game's termination proof and it was put in because the SIMULATION demanded it, not
   * because it sounded good. Unlike a build-up game there is no quantity here that only ever rises,
   * so two careful engines will shuffle: measured, Ledger ran a mean of 65–83 turns and hit the
   * 200-turn backstop in 13% of games, which reads to a player as "nobody won". The levy removes a
   * piece every window while any remain, so the total strictly falls and MAX turns is
   * `levy × (bearers per side × 2 + 1)` by construction — and because it takes from whoever is
   * AHEAD, stalling on a lead is the one thing it punishes.
   */
  levy: number;
}

export const MODES: Record<string, Mode> = {
  ledger: {
    id: 'ledger',
    name: 'Ledger',
    blurb: 'The steady deck, everyone on the back row. A jam — the real currency is which toll you can afford to hand over.',
    pool: 'steady',
    deal: 5,
    table: 1,
    neverTakeBack: false,
    setup: 'flat',
    levy: 20,
  },
  spree: {
    id: 'spree',
    name: 'Spree',
    blurb: 'The wild deck and two bearers already pushed up. Long leaps, contact on turn one, both endings live from the start.',
    pool: 'wild',
    deal: 5,
    table: 1,
    neverTakeBack: false,
    setup: 'forward',
    levy: 28,
  },
  arrears: {
    id: 'arrears',
    name: 'Arrears',
    blurb: 'Two tolls on the table. You may never reclaim the one you just paid — so every turn you also choose what to leave lying around.',
    pool: 'steady',
    deal: 6,
    table: 2,
    neverTakeBack: true,
    setup: 'flat',
    levy: 24,
  },
};

export const MODE_IDS: string[] = ['ledger', 'spree', 'arrears'];
export const DEFAULT_MODE = 'ledger';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
